import { defineStore } from 'pinia';

const initState = () => {
    return {
        session: null,
        cartLoading: false,
        cart: [],
        cartTotals: {
            subtotal: 0,
            discount: 0,
            total: 0
        },
        orderStatus: '',
        paymentdetails: {},
        locks: [],
        authMethod: 'web',
        authSource: 'web',
        loginRedirect: '/',
        nt2only: false,
        hasNT2: false,
        nt2inburgering: false,
        digiplus: false,
        preInschrijvenOk: false,
        loginError: '',
        personalia: {
            kmo: -1,
            NT2: -1,
            NT2Inb: -1,
    
            rrn: '',
            rrnValid: false,
            rrnGenderKnown: false,
            rrnBirthdateKnown: false,
            rrnBirthdate: '',
            rrnGender: '',
            bisNumber: false,
    
            birthDay: '',
            birthMonth: '',
            birthYear: '',
    
            gender: '',
    
            heeftBelgischeNationaliteit: '',
            nationaliteit: '47',
    
            voornaam: '',
            achternaam: '',
    
            geboorteland: '21',
            geboortegemeente: '-1',
    
            straatnaam: '',
            huisnummer: '',
            bus: '',
            postcode: '',
            gemeente: '-1',
            land: '21',
            email: '',
            gsmnummer: '',
            telnummer: '',
    
            werksituatie: '-1',    // gepensioneerd
            hoogstediploma: '-1',  // bachelor
            landdiploma: '21',      // belgie
    
            privacy: '',
            centrumregelement: '',
            nieuwsbrief: '',
        },
    }
}

export const useCartStore = defineStore('cart', () => {
    const { $api } = useNuxtApp();
    const cookieConsent = useCookieConsentStore();
    const typeCheck = useTypeCheck();

    //state
    const _state = ref(initState())

    
    
    //getters
    const session = computed(() => _state.value.session);
    const cart = computed(() => _state.value.cart);
    const cartSubtotal = computed(() => _state.value.cartTotals.subtotal);
    const cartDiscount = computed(() => _state.value.cartTotals.discount);
    const cartTotal = computed(() => _state.value.cartTotals.total);
    const cartLoading = computed(() => _state.value.cartLoading);
    const personalia = computed(() => _state.value.personalia);
    const paymentdetails = computed(() => _state.value.paymentdetails);
    const orderStatus = computed(() => _state.value.orderStatus);
    const locks = computed(() => _state.value.locks);
    const authMethod = computed(() => _state.value.authMethod);
    const authSource = computed(() => _state.value.authSource);
    const loginRedirect = computed(() => _state.value.loginRedirect);
    const nt2only = computed(() => _state.value.nt2only);
    const hasNT2 = computed(() => _state.value.hasNT2);
    const nt2inburgering = computed(() => _state.value.nt2inburgering);
    const digiplus = computed(() => _state.value.digiplus);
    const preInschrijvenOk = computed(() => _state.value.preInschrijvenOk);
    const loginError = computed(() => _state.value.loginError);

    //actions
    const createNewSession = () => new Promise(async(resolve, reject) => {
        const result = await api('/shop/session', {
            method: 'post'
        }).catch(error => reject(error));
        
        if (result?.session)
            resolve(result.session);
    });

    const addCursus = (klas_id, code) => new Promise(async(resolve, reject) => {
        console.log('[PINIA] CartStore: Adding Cursus', klas_id)
        let body = {};
        if (_state.value.session !== null)
            body.session_id = _state.value.session;

        if (code !== null && code !== '')
            body.code = code;

        const result = await api(`/shop/cart/addcursus/${klas_id}`, {
            body,
            method: 'post'
        }).catch(error => reject(error))
        
        //reset payment details because cart changed.
        _state.value.paymentdetails = {};
        resolve();
    })

    const removeCursus = (klas_id) => new Promise(async(resolve, reject) => {
        console.log('[PINIA] CartStore: Removing Cursus', klas_id)
        if (_state.value.session == null) {
            reject('No session');
            return;
        }

        const result = await api(`/shop/cart/removecursus/${klas_id}`, {
            body: {
                session_id: _state.value.session,
            },
            method: 'post'
        }).catch(error => reject(error));

        //reset payment details because cart changed.
        _state.value.paymentdetails = {};
        resolve();
    })

    const fetchPreflight = () => new Promise(async(resolve, reject) => {
        console.log('[PINIA] CartStore: Checking Preflight Status')
        let body = {};
        if (_state.value.session !== null)
            body.session_id = _state.value.session;

        const result = await api('/shop/cart/preflight', {
            body,
            method: 'post'
        }).catch(error => reject(error));

        if (result?.preflight)
            resolve(result.preflight)

        reject('invalid preflight response');
    })

    const fetchCart = () => new Promise(async(resolve, reject) => {
        console.log('[PINIA] CartStore: Fetching server cart')
        let body = {};
        if (_state.value.session !== null)
            body.session_id = _state.value.session;

        const result = await api('/shop/cart', {
            body,
            method: 'post'
        }).catch(error => reject(error));

        if (typeCheck.isObject(result?.personalia))
            _state.value.personalia = result.personalia;

        if (result?.pd)
            _state.value.paymentdetails = result.pd
        else
            _state.value.paymentdetails = {};

        resolve();
    })

    const fetchOrderStatus = () => new Promise(async(resolve, reject) => {
        console.log('[PINIA] CartStore: Fetching Order Status')
        if (_state.value.session === null) {
            reject('invalid session');
            return;
        }

        const result = await api('/shop/cart/status', {
            body: {
                session_id: _state.value.session
            },
            method: 'post'
        }).catch(error => reject(error));

        if (!result.status) {
            reject('server did not provide status');
            return;
        }

        _state.value.orderStatus = result.status;
        resolve();
    });

    const startPayment = () => new Promise(async(resolve, reject) => {
        console.log('[PINIA] CartStore: Start payment')
        if (_state.value.session === null) {
            reject('invalid session');
            return;
        }

        const result = await api('/shop/startpayment', {
            body: {
                session_id: _state.value.session
            },
            method: 'post',
        }).catch(error => reject(error));

        if (result?.pd)
            _state.value.paymentdetails = result.pd;
        else
            _state.value.paymentdetails = {};

        resolve();
    });

    const startInschrijving = () => new Promise(async(resolve ,reject) => {
        console.log('[PINIA] CartStore: Start Inschrijving')
        if (_state.value.session === null) {
            reject('invalid session');      
            return;
        }

        const result = await api('/shop/startinschrijving', {
            body: {
                session_id: _state.value.session,
            },
            method: 'post',
        }).catch(error => reject(error));

        resolve();
    })

    const pushPersonalia = () => new Promise(async(resolve, reject) => {
        if (_state.value.session === null) {
            reject('invalid session');      
            return;
        }  

        console.log('[PINIA] CartStore: PushPersonalia');
        const result = await api('/shop/personalia', {
            body: {
                session_id: _state.value.session,
                personalia: JSON.stringify(_state.value.personalia)
            },
            method: 'post'
        }).catch(error => reject(error));

        resolve();
    })

    const startAcmLogin = () => startAuth('acm');
    const startAzureLogin = () => startAuth('azure');
    const setWebLogin = () => _state.value.authMethod = 'web';

    const destroyCart = () => {
        _state.value = initState();
        setSession(null);
    }


    // Utility:

    const startAuth = (authMethod) => new Promise(async(resolve, reject) => {
        console.log('[PINIA] CartStore: Start Auth')
        if (_state.value.session === null)
            reject('invalid session');
        
        setAuthMethod(authMethod);

        const result = await $api(`/auth/${authMethod}` , {
            body: {
                t: 'cart',
                session_id: _state.value.session,
                return_url: getAppUrl('/inloggen/check'),
            },
            method: 'post'
        }).catch(error => reject(error));
        
        if (result?.auth_url);
            resolve(result.auth_url)
        
        reject('No auth URL');
    })

    const updateStateFromServer = (serverState) => {
        console.log(serverState['nt2only'])
        const fields = [
            'cart',
            'cartTotals',
            'nt2only',
            'hasNT2',
            'nt2inburgering',
            'digiplus',
            'locks',
            'preInschrijvenOk'
        ];

        if (serverState.session)
            setSession(serverState.session);

        fields.forEach((field) => {
            if (serverState[field])
                _state.value[field] = serverState[field];
        });
    }

    const handleErrorFromServer = (error) => {
        if (error.data.session && error.data.session == null)
            _state.value.session = null;

        if (error.data.cart)
            _state.value.cart = error.data.cart;

        if (error.data.cartTotals)
            _state.value.cartTotals = error.data.cartTotals;

        if (error.data.personalia)
            _state.value.personalia = error.data.personalia;

        _state.value.paymentdetails = {};
    }

    const api = async(url, options) => new Promise(async(resolve, reject) => {
        _state.value.cartLoading = true;
        const result = await $api(url, options).catch(error => {
            handleErrorFromServer(error);
            _state.value.cartLoading = false;
            reject(error);
        })

        if (!result) {
            _state.value.cartLoading = false;
            return;
        }

        //authsource veranderd ?
        if (result?.s)
            _state.value.authSource = result.s;

        // is onze auth wel in orde ?
        if (_state.value.authMethod != 'web' && _state.value.authMethod != _state.value.authSource) {
            _state.value.cartLoading = false;
            setAuthMethod('web');
            //reject({authmismatch: true});
        }

        //state updaten
        updateStateFromServer(result);

        _state.value.cartLoading = false;

        resolve(result);
    });

    const getAppUrl = (path = '') => {
        let hostinfo = window.location.href.match(/^(([^:\/?#]+):)?(\/\/([^\/?#]*))?([^?#]*)(\?([^#]*))?(#(.*))?/);
        if (hostinfo.length > 0)
            return hostinfo[1] + hostinfo[3] + path;
        return '';
    };

    const setSession = (session_id) => {
        _state.value.session = session_id;

        if (import.meta.client) {
            const cart_session_id = useCookie('volt_cart_session_id', {
                maxAge: 86400,  //24u
                path: '/',
                sameSite: 'lax',
            });

            if (session_id !== null) {
                cart_session_id.value = session_id;

                //als clarity geladen is, session_id doorgeven aan clarity voor debug
                if (window.clarity && cookieConsent.cookies.analytics)
                    window.clarity("set", "session_id", session_id);
            } else {
                // als sessie null is verwijder dan de cookie.
                cart_session_id.value = null;
            }
        }
    }

    const setAuthMethod = (method) => {
        _state.value.authMethod = method;
        if (import.meta.client) {
            const cart_auth_method = useCookie('volt_cart_auth_method', {
                path: '/',
            });
            cart_auth_method.value = method
        }
    }

    const setLoginRedirect = (path, resetCookie = false) => {
        _state.value.loginRedirect = path;
        if (import.meta.client) {
            const cart_loginRedirect = useCookie('volt_login_redirect', {
                path: '/',
            });
            cart_loginRedirect.value = resetCookie ? null : path;
        }
    }

    const setLoginError = (error) => loginError.value = error;
    const setLocks = (newLocks) => locks.value = newLocks;

    const updatePersonalia = (personalia) => {
        //merge the old personalia with the new personalia and commit to state
        _state.value.personalia = {
            ..._state.value.personalia,
            ...personalia
        };
    }

    return {
        _state,

        session,
        cart,
        cartSubtotal,
        cartDiscount,
        cartTotal,
        cartLoading,
        personalia,
        paymentdetails,
        orderStatus,
        locks,
        authMethod,
        authSource,
        loginRedirect,
        hasNT2,
        nt2only,
        nt2inburgering,
        digiplus,
        preInschrijvenOk,
        loginError,

        createNewSession,
        addCursus,
        removeCursus,
        fetchPreflight,
        fetchCart,
        fetchOrderStatus,
        startPayment,
        startInschrijving,
        pushPersonalia,
        startAcmLogin,
        startAzureLogin,
        setWebLogin,
        destroyCart,
        setAuthMethod,
        setLoginRedirect,
        setSession,
        setLoginError,
        setLocks,
        updatePersonalia,
    }

})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot))
} 